import { useState } from 'react';
import { Download } from 'lucide-react';
import { PLATFORMS, TOOLS } from './_tools';
import { useToolRelease, getToolDownloadUrl } from '@/data/toolRelease';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/helpers/toast';

// Which download to put first: the computer this page is open on, when we can tell.
const detectPlatform = () => {
  const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent;
  if (/win/i.test(platform)) return 'windows';
  if (/mac/i.test(platform)) return 'mac';
  return null;
};

const formatSize = (bytes) => `${Math.round(bytes / 1024 / 1024)} MB`;

const formatDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const ToolCard = ({ tool }) => {
  const release = useToolRelease(tool.id);
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(null);
  const detected = detectPlatform();

  const assets = release.data?.assets ?? [];
  const ordered = [...assets].sort((a, b) => (b.platform === detected) - (a.platform === detected));

  const download = async (platform) => {
    setDownloading(platform);
    try {
      window.location.href = await getToolDownloadUrl(tool.id, platform);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Download failed', description: err.message });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-start gap-4 space-y-0">
        <img src={tool.icon} alt="" className="size-12 shrink-0 rounded-xl" />
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <CardTitle>{tool.name}</CardTitle>
            {release.data && <Badge variant="secondary">v{release.data.version}</Badge>}
          </div>
          <CardDescription>{tool.description}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3 text-sm">
        {release.isLoading && <p className="text-muted-foreground">Finding the latest release…</p>}
        {release.error && <p className="text-destructive">Couldn&apos;t load the latest release.</p>}
        {release.data === null && <p className="text-muted-foreground">No release yet. One is published each time {tool.name}&apos;s main branch changes.</p>}
        {release.data && (
          <p className="text-muted-foreground">
            Released {formatDate(release.data.published_at)}.{tool.selfUpdates && ` ${tool.name} offers to upgrade itself when a newer one is out.`}
          </p>
        )}
        {ordered.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            {ordered.map((asset) => PLATFORMS[asset.platform] && <li key={asset.platform}>{PLATFORMS[asset.platform].install(tool.name)}</li>)}
            {tool.afterInstall && <li>{tool.afterInstall}</li>}
          </ul>
        )}
      </CardContent>
      {ordered.length > 0 && (
        <CardFooter className="flex flex-wrap gap-2">
          {ordered.map((asset, i) => (
            <Button
              key={asset.platform}
              variant={i === 0 && asset.platform === detected ? 'default' : 'outline'}
              onClick={() => download(asset.platform)}
              disabled={downloading !== null}
              title={`${asset.name} · ${formatSize(asset.size)}`}
            >
              <Download />
              {downloading === asset.platform ? 'Starting…' : `Download for ${PLATFORMS[asset.platform]?.label ?? asset.platform}`}
            </Button>
          ))}
          {ordered[0] && <span className="text-xs text-muted-foreground">{PLATFORMS[ordered[0].platform]?.detail}</span>}
        </CardFooter>
      )}
    </Card>
  );
};

const ToolsPage = () => {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tools</h1>
        <p className="text-sm text-muted-foreground">Internal apps for Zebel staff. Each download is the latest build.</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {TOOLS.map((tool) => (
          <ToolCard key={tool.id} tool={tool} />
        ))}
      </div>
    </div>
  );
};

export default ToolsPage;
